import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import classNames from "classnames";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import ConditionalComponent from "../ConditionalComponent";
import { installationRules } from "../Rules";
import { currentInstallation, getChaudList, scrbData, sortOptions, translateList, validateYear } from "../ScrbTools";
import moment from "moment";

DonneesTechniques.propTypes = {
    editable: PropTypes.bool.isRequired
}

function DonneesTechniques(props) {

    const installation = useHookstate(currentInstallation);
    const data = useHookstate(scrbData).get();
    const [appareilLabel, setAppareilLabel] = useState("");
    const [marqueChaudiereSuggestions, setMarqueChaudiereSuggestions] = useState([]);
    useEffect(() => {
        if (installation.get().genreInstallationId) {
            setAppareilLabel(data.installationColors[installation.get().genreInstallationId].appareilLabel);
        }
        setMarqueChaudiereSuggestions(data.chaudiereMarque);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installation]);

    const onObjectChange = (event, object) => {
        installation[object][event.target.name].set(event.target.value);
    }

    const onYearChange = (event, object) => {
        if (event.target.value !== null) {
            const mom = moment(event.target.value).add(1, 'day');
            installation[object][event.target.name].set(mom.toDate());
        }
    }

    return (
        <div className="flex flex-column pt-5">
            <div className="flex flex-column">
                <div className="flex flex-column">
                    <h2 className="text-xl font-semibold mb-4">
                        <i className={data.installationColors[installation.get().genreInstallationId].icon} style={{ marginRight: '0.5rem' }}></i>
                        {I18n.get(appareilLabel)}
                    </h2>
                    <div className="flex gap-4 w-full">
                        <div className="flex flex-column w-full pt-2 gap-4">
                            <FloatLabel >
                                <InputText
                                    id="numSerie"
                                    name="numSerie"
                                    value={installation.get().chaudiere?.numSerie || ""}
                                    onChange={(event) => { onObjectChange(event, "chaudiere") }}
                                    className={classNames(
                                        "w-full"
                                    )}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={50}
                                />
                                <label htmlFor="numSerie">{I18n.get("numSerie")}</label>
                            </FloatLabel>
                            <span className="p-float-label">
                                <AutoComplete
                                    inputId="marqueChaudiere"
                                    name="marqueChaudiere"
                                    value={installation.get().chaudiere?.marqueChaudiere || ""}
                                    onChange={(event) => { onObjectChange(event, "chaudiere") }}
                                    suggestions={marqueChaudiereSuggestions}
                                    completeMethod={(event) => {
                                        setMarqueChaudiereSuggestions(data.chaudiereMarque.filter(m => m.toLowerCase().includes(event.query.toLowerCase())));
                                    }}
                                    className={classNames(
                                        { "p-invalid": !installation.get().chaudiere.marqueChaudiere },
                                        "w-full"
                                    )}
                                    inputStyle={{ width: '100%' }}
                                    disabled={!props.editable}
                                    maxLength={50}
                                />
                                <label htmlFor="marqueChaudiere">{I18n.get("marqueChaudiere")} {I18n.get(appareilLabel)}</label>
                            </span>
                            <FloatLabel >
                                <InputText
                                    id="modelChaudiere"
                                    name="modelChaudiere"
                                    value={installation.get().chaudiere?.modelChaudiere || ""}
                                    onChange={(event) => { onObjectChange(event, "chaudiere") }}
                                    className={classNames(
                                        { "p-invalid": !installation.get().chaudiere.modelChaudiere },
                                        "w-full"
                                    )}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={50}
                                />
                                <label htmlFor="modelChaudiere">{I18n.get("modelChaudiere")} {I18n.get(appareilLabel)}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Calendar
                                    value={installation.get().chaudiere?.anneeConstructChaudiere}
                                    name="anneeConstructChaudiere"
                                    onChange={(event) => { onYearChange(event, "chaudiere") }}
                                    view="year"
                                    dateFormat="yy"
                                    maxDate={new Date()}
                                    disabled={!props.editable}
                                    className={classNames("w-full", {
                                        "p-invalid": [null, undefined].includes(installation.get().chaudiere.anneeConstructChaudiere) ||
                                            !validateYear(installation.get().chaudiere.anneeConstructChaudiere)
                                    })}
                                />
                                <label htmlFor="anneeConstructChaudiere">{I18n.get("anneeConstructChaudiere")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Calendar
                                    value={installation.get().chaudiere?.anneeEntreeChaudiere}
                                    name="anneeEntreeChaudiere"
                                    onChange={(event) => { onYearChange(event, "chaudiere") }}
                                    view="year"
                                    dateFormat="yy"
                                    maxDate={new Date()}
                                    disabled={!props.editable}
                                    className={classNames("w-full", {
                                        "p-invalid": [null, undefined].includes(installation.get().chaudiere.anneeEntreeChaudiere) ||
                                            installation.get().chaudiere.anneeEntreeChaudiere < installation.get().chaudiere.anneeConstructChaudiere ||
                                            !validateYear(installation.get().chaudiere.anneeEntreeChaudiere)
                                    })}
                                />
                                <label htmlFor="anneeEntreeChaudiere">{I18n.get("anneeEntreeChaudiere")}</label>
                            </FloatLabel>
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["climOnly"]}
                                component={<>
                                    <FloatLabel >
                                        <InputNumber
                                            id="chargeNominale"
                                            name="chargeNominale"
                                            value={installation.get().chaudiere?.chargeNominale}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            useGrouping={false}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            className={classNames(
                                                { "p-invalid": [null, undefined].includes(installation.get().chaudiere.chargeNominale) },
                                                "w-full"
                                            )}
                                            autoComplete="off"
                                            min={0}
                                            inputStyle={{ width: '100%' }}
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="chargeNominale">{I18n.get("chargeNominale")}</label>
                                    </FloatLabel>
                                    <FloatLabel >
                                        <InputNumber
                                            id="puissanceFrigo"
                                            name="puissanceFrigo"
                                            value={installation.get().chaudiere?.puissanceFrigo}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            useGrouping={false}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            className={classNames(
                                                { "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceFrigo) },
                                                "w-full"
                                            )}
                                            autoComplete="off"
                                            min={0}
                                            inputStyle={{ width: '100%' }}
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceFrigo">{I18n.get("puissanceFrigo")}</label>
                                    </FloatLabel>
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceElec"
                                            name="puissanceElec"
                                            value={installation.get().chaudiere?.puissanceElec}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            useGrouping={false}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            className={classNames(
                                                { "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceElec) },
                                                "w-full"
                                            )}
                                            autoComplete="off"
                                            inputStyle={{ width: '100%' }}
                                            min={0}
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceElec">{I18n.get("puissanceElec")}</label>
                                    </FloatLabel>
                                </>}
                            />
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["typeChaudiere"]}
                                component={
                                    <FloatLabel>
                                        <Dropdown
                                            value={installation.get().chaudiere.typeChaudiere}
                                            onChange={(e) => installation.chaudiere.typeChaudiere.set(e.value)}
                                            options={sortOptions(translateList(getChaudList(installation.get().genreInstallationId)))}
                                            optionValue="id"
                                            optionLabel="label"
                                            placeholder={I18n.get("typeChaudiere")}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": !installation.get().chaudiere.typeChaudiere
                                            })}
                                            disabled={!props.editable}
                                        />
                                        <label htmlFor="typeChaudiere">{I18n.get("typeChaudiere")}</label>
                                    </FloatLabel>
                                }
                            />
                            {<ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["puissanceChaudiere"]}
                                component={<>
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceMinChaudiere"
                                            name="puissanceMinChaudiere"
                                            value={installation.get().chaudiere?.puissanceMinChaudiere}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            useGrouping={false}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceMinChaudiere)
                                            })}
                                            autoComplete="off"
                                            min={0}
                                            inputStyle={{ width: '100%' }}
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceMinChaudiere">{I18n.get("puissanceMinChaudiere")}</label>
                                    </FloatLabel>
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceMaxChaudiere"
                                            name="puissanceMaxChaudiere"
                                            value={installation.get().chaudiere?.puissanceMaxChaudiere}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            useGrouping={false}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceMaxChaudiere) ||
                                                    installation.get().chaudiere.puissanceMaxChaudiere < installation.get().chaudiere.puissanceMinChaudiere
                                            })}
                                            autoComplete="off"
                                            inputStyle={{ width: '100%' }}
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceMaxChaudiere">{I18n.get("puissanceMaxChaudiere")}</label>
                                    </FloatLabel>
                                </>}
                            />}
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["isGaz"]}
                                component={
                                    <FloatLabel>
                                        <Dropdown
                                            id="chaudAppareil"
                                            name="chaudAppareil"
                                            value={installation.get().chaudiere.chaudAppareil}
                                            onChange={(e) => installation.chaudiere.chaudAppareil.set(e.value)}
                                            options={data.typeAppareilList}
                                            placeholder={I18n.get("chaudAppareil")}
                                            filter
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": !installation.get().chaudiere.chaudAppareil
                                            })}
                                            disabled={!props.editable}
                                        />
                                        <label htmlFor="chaudAppareil">{I18n.get("chaudAppareil")}</label>
                                    </FloatLabel>
                                }
                            />
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["pompeOnly"]}
                                component={<>
                                    <FloatLabel>
                                        <InputNumber
                                            id="chargeChauffageBatiment"
                                            name="chargeChauffageBatiment"
                                            value={installation.get().chaudiere.chargeChauffageBatiment}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.chargeChauffageBatiment)
                                            })}
                                            min={0}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            inputStyle={{ width: '100%' }}
                                            suffix="kW"
                                            autoComplete="off"
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="chargeChauffageBatiment">{I18n.get("chargeChauffageBatiment")}</label>
                                    </FloatLabel>
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceNecessaireEauChaude"
                                            name="puissanceNecessaireEauChaude"
                                            value={installation.get().chaudiere.puissanceNecessaireEauChaude}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceNecessaireEauChaude)
                                            })}
                                            inputStyle={{ width: '100%' }}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            min={0}
                                            suffix="kW"
                                            autoComplete="off"
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceNecessaireEauChaude">{I18n.get("puissanceNecessaireEauChaude")}</label>
                                    </FloatLabel>
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceSelonNorme"
                                            name="puissanceSelonNorme"
                                            value={installation.get().chaudiere.puissanceSelonNorme}
                                            onValueChange={(event) => { onObjectChange(event, "chaudiere") }}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceSelonNorme)
                                            })}
                                            inputStyle={{ width: '100%' }}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            suffix="kW"
                                            min={0}
                                            autoComplete="off"
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceSelonNorme">{I18n.get("puissanceSelonNorme")}</label>
                                    </FloatLabel>
                                </>}
                            />
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["gazMazoutOnly"]}
                                component={<>
                                    <FloatLabel>
                                        <Dropdown
                                            value={installation.get().bruleur?.typeBruleur}
                                            name="typeBruleur"
                                            onChange={(event) => onObjectChange(event, "bruleur")}
                                            options={sortOptions(translateList(data.typeBruleurList))}
                                            optionValue="id"
                                            optionLabel="label"
                                            placeholder={I18n.get("typeBruleur")}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": !installation.get().bruleur?.typeBruleur
                                            })}
                                            disabled={!props.editable}
                                        />
                                        <label htmlFor="typeBruleur">{I18n.get("typeBruleur")}</label>
                                    </FloatLabel>
                                    <div className="flex flex-row pt-5">
                                        <label>{I18n.get("bruleurSame")}</label>&nbsp;
                                        <InputSwitch
                                            id="bruleurSame"
                                            checked={installation.get().bruleurSame}
                                            onClick={(e) => {
                                                installation.bruleurSame.set(e.target.checked);
                                            }}
                                            disabled={!props.editable}
                                        />
                                    </div>
                                </>
                                }
                            />
                        </div>
                    </div>
                </div>

                <ConditionalComponent
                    objState={installation}
                    rules={installationRules}
                    id={["gazMazoutOnly"]}
                    component={
                        !installation.get().bruleurSame && (
                            <div className="flex flex-column pt-6">
                                <h2 className="text-xl font-semibold mb-4">
                                    <i className="fa-light fa-fire mr-2" style={{ marginRight: '0.5rem' }}></i>
                                    {I18n.get("bruleur")}
                                </h2>
                                <div className="flex gap-4 w-full ">
                                    <div className="flex flex-column w-full pt-2 gap-4">
                                        <FloatLabel >
                                            <InputText
                                                id="marqueBruleur"
                                                name="marqueBruleur"
                                                value={installation.get().bruleur?.marqueBruleur || ""}
                                                onChange={(event) => { onObjectChange(event, "bruleur") }}
                                                className={classNames(
                                                    { "p-invalid": !installation.get().bruleur?.marqueBruleur },
                                                    "w-full"
                                                )}
                                                autoComplete="off"
                                                disabled={!props.editable}
                                                maxLength={50}
                                            />
                                            <label htmlFor="marqueBruleur">{I18n.get("marqueBruleur")}</label>
                                        </FloatLabel>
                                        <FloatLabel >
                                            <InputText
                                                id="modelBruleur"
                                                name="modelBruleur"
                                                value={installation.get().bruleur?.modelBruleur || ""}
                                                onChange={(event) => { onObjectChange(event, "bruleur") }}
                                                className={classNames(
                                                    { "p-invalid": !installation.get().bruleur?.modelBruleur },
                                                    "w-full"
                                                )}
                                                autoComplete="off"
                                                disabled={!props.editable}
                                                maxLength={50}
                                            />
                                            <label htmlFor="modelBruleur">{I18n.get("modelBruleur")}</label>
                                        </FloatLabel>
                                        <FloatLabel>
                                            <Calendar
                                                name="anneeConstructBruleur"
                                                value={installation.get().bruleur?.anneeConstructBruleur}
                                                onChange={(event) => { onYearChange(event, "bruleur") }}
                                                view="year"
                                                dateFormat="yy"
                                                maxDate={new Date()}
                                                disabled={!props.editable}
                                                className={classNames("w-full", {
                                                    "p-invalid": [null, undefined].includes(installation.get().bruleur.anneeConstructBruleur) ||
                                                        !validateYear(installation.get().bruleur.anneeConstructBruleur)
                                                })}
                                            />
                                            <label htmlFor="anneeConstructBruleur">{I18n.get("anneeConstructBruleur")}</label>
                                        </FloatLabel>
                                        <FloatLabel>
                                            <Calendar
                                                name="anneeEntreeBruleur"
                                                value={installation.get().bruleur?.anneeEntreeBruleur}
                                                onChange={(event) => { onYearChange(event, "bruleur") }}
                                                view="year"
                                                dateFormat="yy"
                                                maxDate={new Date()}
                                                disabled={!props.editable}
                                                className={classNames("w-full", {
                                                    "p-invalid": [null, undefined].includes(installation.get().bruleur.anneeEntreeBruleur) ||
                                                        installation.get().bruleur.anneeEntreeBruleur < installation.get().bruleur.anneeConstructBruleur ||
                                                        !validateYear(installation.get().bruleur.anneeEntreeBruleur)
                                                })}
                                            />
                                            <label htmlFor="anneeEntreeBruleur">{I18n.get("anneeEntreeBruleur")}</label>
                                        </FloatLabel>
                                        <FloatLabel>
                                            <InputNumber
                                                id="puissanceMinBruleur"
                                                name="puissanceMinBruleur"
                                                value={installation.get().bruleur?.puissanceMinBruleur}
                                                onValueChange={(event) => { onObjectChange(event, "bruleur") }}
                                                useGrouping={false}
                                                minFractionDigits={1}
                                                maxFractionDigits={1}
                                                className={classNames({
                                                    "w-full": true,
                                                    "p-invalid": [null, undefined].includes(installation.get().bruleur?.puissanceMinBruleur)
                                                })}
                                                min={0}
                                                inputStyle={{ width: '100%' }}
                                                autoComplete="off"
                                                disabled={!props.editable}
                                                maxLength={52}
                                            />
                                            <label htmlFor="puissanceMinBruleur">{I18n.get("puissanceMinBruleur")}</label>
                                        </FloatLabel>
                                        <FloatLabel>
                                            <InputNumber
                                                id="puissanceMaxBruleur"
                                                name="puissanceMaxBruleur"
                                                value={installation.get().bruleur?.puissanceMaxBruleur}
                                                onValueChange={(event) => { onObjectChange(event, "bruleur") }}
                                                useGrouping={false}
                                                minFractionDigits={1}
                                                maxFractionDigits={1}
                                                className={classNames({
                                                    "w-full": true,
                                                    "p-invalid": [null, undefined].includes(installation.get().bruleur?.puissanceMaxBruleur) ||
                                                        installation.get().bruleur.puissanceMaxBruleur < installation.get().bruleur.puissanceMinBruleur
                                                })}
                                                min={0}
                                                inputStyle={{ width: '100%' }}
                                                autoComplete="off"
                                                disabled={!props.editable}
                                                maxLength={52}
                                            />
                                            <label htmlFor="puissanceMaxBruleur">{I18n.get("puissanceMaxBruleur")}</label>
                                        </FloatLabel>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                />
            </div>
        </div>
    );
}

export default DonneesTechniques;